/**
 * Purchase Requisitions module — service layer.
 *
 * Raises and maintains PR documents — internal "pay this vendor" records.
 * Two creation paths: manual single-vendor PRs (`createManualRequisition`) and
 * order-side vendor-grouped PRs picked from a quote's lines
 * (`createVendorGroupedRequisitions`, one PR per vendor). Bridges the UI to the
 * repository. PR numbers are server-allocated (`PR` kind) via the shared
 * sequence allocator. The localStorage mirror was deleted in the dual-path
 * retirement (Phase 2b) — local dev runs against the emulator
 * (`npm run dev:emu`).
 */

import { repositories } from '../../../data/repositories';
import { round2 } from '../../../../shared/money';
import { byCreatedDesc } from '../../../utils/sort';
import { allocateSequenceNumber } from '../../../lib/sequenceAllocator';
import { groupLinesByVendor, type VendorPickLine } from '../../orders/services/vendorGrouping';
import type {
  PurchaseRequisition,
  RequisitionLine,
  RequisitionStatus,
  EntityId,
} from '../../../types';

const persist = async (pr: PurchaseRequisition): Promise<void> => {
  await repositories.requisitions.save(pr);
};

export const listRequisitions = async (): Promise<PurchaseRequisition[]> => {
  const all = await repositories.requisitions.list();
  return all.sort(byCreatedDesc);
};

export const fetchRequisition = async (id: EntityId): Promise<PurchaseRequisition | null> => {
  return repositories.requisitions.findById(id);
};

export const listRequisitionsByOrder = async (
  projectId: EntityId,
): Promise<PurchaseRequisition[]> => {
  const all = await repositories.requisitions.findByOrder(projectId);
  return all.sort(byCreatedDesc);
};

/** Input for a manually-typed PR — used for parts / extrusions / accessories
 *  that aren't rolled up from a quote. Lines are free-text. */
export interface ManualRequisitionInput {
  vendorId: EntityId;
  vendorName: string;
  /** Free-text line items. Each line MUST carry description + qty (≥ 1)
   *  + unitCost (≥ 0). UOM is optional and defaults to 'each'. */
  lines: Array<{
    description: string;
    uom?: string;
    qty: number;
    unitCost: number;
  }>;
  /** Optional link to a quote — usually unused (manual PR is for stock /
   *  job-tied parts, not a quote's adhocs). When present we snapshot the
   *  quote's customer name onto the PR. */
  sourceQuoteId?: EntityId;
  /** Optional link to an order for cost-accounting against a specific job. */
  projectId?: EntityId;
  /** Customer name — auto-filled from the linked quote/order at the call
   *  site if not provided. Empty string is fine for stock PRs. */
  customerName?: string;
  /** Optional free-text note carried onto the finance document. */
  notes?: string;
}

/**
 * Create a single manual PR for parts / extrusions / accessories. Unlike
 * `createVendorGroupedRequisitions`, this does NOT group by vendor — it produces
 * exactly one PR for the chosen vendor. The purchaser is responsible for
 * picking which lines belong on which PR.
 *
 * Returns the created PR. Throws if vendorId / vendorName / a non-empty
 * lines array are missing — those are required for a meaningful PR. Empty
 * descriptions and zero quantities are filtered out before persist.
 */
export const createManualRequisition = async (
  input: ManualRequisitionInput,
  actor: string,
): Promise<PurchaseRequisition> => {
  if (!input.vendorId || !input.vendorName?.trim()) {
    throw new Error('A supplying vendor is required.');
  }
  const cleanedLines: RequisitionLine[] = (input.lines ?? [])
    .filter((l) => l && l.description?.trim() && (l.qty || 0) > 0)
    .map((l) => {
      const qty = Math.max(0, l.qty || 0);
      const unitCost = round2(Math.max(0, l.unitCost || 0));
      return {
        description: l.description.trim(),
        uom: (l.uom || 'each').trim() || 'each',
        qty,
        unitCost,
        lineCost: round2(unitCost * qty),
      };
    });
  if (cleanedLines.length === 0) {
    throw new Error('A purchase requisition needs at least one line item.');
  }

  const totalCost = round2(cleanedLines.reduce((sum, l) => sum + l.lineCost, 0));
  const { number } = await allocateSequenceNumber('PR');
  const now = new Date().toISOString();

  const pr: PurchaseRequisition = {
    id: `pr_${crypto.randomUUID()}`,
    requisitionNumber: number,
    status: 'DRAFT',
    source: 'manual',
    vendorId: input.vendorId,
    vendorName: input.vendorName.trim(),
    // Optional links — omit entirely when absent (Firestore rejects `undefined`).
    ...(input.sourceQuoteId ? { sourceQuoteId: input.sourceQuoteId } : {}),
    ...(input.projectId ? { orderId: input.projectId } : {}),
    customerName: (input.customerName ?? '').trim(),
    lines: cleanedLines,
    totalCost,
    ...(input.notes?.trim() ? { notes: input.notes.trim() } : {}),
    createdAt: now,
    createdBy: actor,
    updatedAt: now,
    updatedBy: actor,
  };

  await persist(pr);
  return pr;
};

export interface CreateVendorGroupedInput {
  orderId?: EntityId;
  sourceQuoteId?: EntityId;
  customerName: string;
  lines: VendorPickLine[];
}

/**
 * Create one manual, order-linked PR per distinct vendor from a flat set of
 * picker lines. $0/TBD costs allowed; each line carries its sourceQuoteLineId
 * tag. Returns the created PRs.
 */
export const createVendorGroupedRequisitions = async (
  input: CreateVendorGroupedInput,
  actor: string,
): Promise<PurchaseRequisition[]> => {
  const drafts = groupLinesByVendor(input.lines);
  if (drafts.length === 0) {
    throw new Error('Tick at least one line and assign a vendor to raise a requisition.');
  }
  return Promise.all(
    drafts.map(async (draft) => {
      const { number } = await allocateSequenceNumber('PR');
      const now = new Date().toISOString();
      const pr: PurchaseRequisition = {
        id: `pr_${crypto.randomUUID()}`,
        requisitionNumber: number,
        status: 'DRAFT',
        source: 'manual',
        vendorId: draft.vendorId,
        vendorName: draft.vendorName,
        ...(input.sourceQuoteId ? { sourceQuoteId: input.sourceQuoteId } : {}),
        ...(input.orderId ? { orderId: input.orderId } : {}),
        customerName: (input.customerName ?? '').trim(),
        lines: draft.lines,
        totalCost: draft.totalCost,
        createdAt: now,
        createdBy: actor,
        updatedAt: now,
        updatedBy: actor,
      };
      await persist(pr);
      return pr;
    }),
  );
};

export interface StatusChangeMeta {
  cancelledReason?: string;
}

/**
 * Move a requisition through its workflow (Draft → Submitted → Approved →
 * Paid, or → Cancelled), stamping the matching timestamp + actor. The UI
 * gates which transitions each role may trigger.
 */
export const updateRequisitionStatus = async (
  id: EntityId,
  status: RequisitionStatus,
  actor: string,
  meta?: StatusChangeMeta,
): Promise<PurchaseRequisition> => {
  const current = await fetchRequisition(id);
  if (!current) throw new Error(`Requisition ${id} not found.`);
  const now = new Date().toISOString();
  const updated: PurchaseRequisition = {
    ...current,
    status,
    updatedAt: now,
    updatedBy: actor,
  };
  if (status === 'SUBMITTED') {
    updated.submittedAt = now;
    updated.submittedBy = actor;
  } else if (status === 'APPROVED') {
    updated.approvedAt = now;
    updated.approvedBy = actor;
  } else if (status === 'PAID') {
    updated.paidAt = now;
    updated.paidBy = actor;
  } else if (status === 'CANCELLED') {
    updated.cancelledAt = now;
    updated.cancelledBy = actor;
    if (meta?.cancelledReason) updated.cancelledReason = meta.cancelledReason;
  }
  await persist(updated);
  return updated;
};
